import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, ASSETS, clearSession } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const authCtx = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const validate = () => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(password)) return "Password must contain an uppercase letter.";
    if (!/[0-9]/.test(password)) return "Password must contain a number.";
    if (password !== confirm) return "Passwords do not match.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      // Every existing JWT for this user is now invalidated — force
      // sign-out on this browser too.
      clearSession();
      if (authCtx?.logout) authCtx.logout();
      setDone(true);
      toast.success("Password updated. Please sign in with your new password.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (detail === "invalid_or_expired_token" || detail === "token_already_used") {
        toast.error("This link is invalid or has already been used. Request a new one.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Could not reset password. Please request a new link.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-black text-white" data-testid="reset-missing-token">
        <div className="max-w-md text-center space-y-4">
          <div className="font-anton text-3xl">Reset link missing</div>
          <p className="text-white/60">This page needs a token from the email we sent you.</p>
          <Link to="/forgot-password" className="btn-primary" data-testid="reset-request-new">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden bg-black" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(239,43,43,0.15),transparent_55%)]" />
      <div className="w-full max-w-md relative">
        <Link to="/login" className="font-mono text-[10px] tracking-[0.3em] text-white/50 hover:text-white">← BACK TO SIGN IN</Link>
        <div className="mt-6 hairline rounded-2xl bg-white/[0.02] p-10">
          <img src={ASSETS.logo} alt="DJ SPY" className="h-12 w-auto" />
          <div className="mt-6">
            <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">// PASSWORD RESET</div>
            <div className="font-anton text-3xl text-white leading-none mt-2">Set a new password</div>
          </div>
          {done ? (
            <p className="mt-8 text-white/70" data-testid="reset-done">Redirecting you to sign in…</p>
          ) : (
            <form className="mt-8 space-y-4" onSubmit={onSubmit} data-testid="reset-form">
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">NEW PASSWORD</label>
                <input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 chars, 1 uppercase, 1 number" dir="ltr" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">CONFIRM</label>
                <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password" dir="ltr" />
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit" className="btn-primary w-full justify-center">
                {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                {loading ? "Updating…" : "Update password"}
              </button>
              <p className="text-white/40 text-xs">After you reset, every device signed into this account will be signed out.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
